import {
  selectOrganizationId,
  useCreateTourFromUrlMutation,
  useGetDemosQuery,
} from '@carboncopyco/data';
import type { Folder, Tour } from '@carboncopyco/types';
import { isDesktop } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CreateEntityModal } from '../../Demo';
import { CreateTourFromScratch } from '../CreateTourFromScratch';
import { CreateTourModal } from '../CreateTourModal';

type TourCaptureModalProps = {
  showCreateTourModal: boolean;
  selectedFrameIndex?: number;
  originalTourId?: string;
  demoIdsTourIsBasedOn?: number[];
  hide: () => void;
  navigateToWebTourCapture: ({
    appUrl,
    playerToken,
    fileServiceToken,
    tourId,
    originalTourId,
    insertIndex,
    workspaceId,
    folderId,
  }: {
    appUrl: string;
    playerToken?: string;
    fileServiceToken?: string;
    tourId?: string;
    originalTourId?: string;
    insertIndex?: number;
    workspaceId?: string;
    folderId?: Folder['id'];
  }) => void;
  navigateToTourFromDemo: (id: number, originalTourId?: number) => void;
  goToDemo: (demoId: string, workspaceId?: string | undefined) => void;
  handleOpenRecorder: () => void;
  title?: string;
  description?: string;
  goToCreateOverlayDemoPage: () => void;
  folderId?: Folder['id'];
};

export const TourCaptureModals = ({
  showCreateTourModal,
  hide,
  demoIdsTourIsBasedOn,
  originalTourId,
  selectedFrameIndex = 0,
  title,
  description,
  navigateToWebTourCapture,
  navigateToTourFromDemo,
  goToDemo,
  handleOpenRecorder,
  goToCreateOverlayDemoPage,
  folderId,
}: TourCaptureModalProps) => {
  const organizationId = useSelector(selectOrganizationId);

  const [
    showCreateTourFromDemoModal,
    {
      setTrue: setTrueShowCreateTourFromDemoModal,
      setFalse: setFalseShowCreateTourFromDemoModal,
    },
  ] = useBoolean();

  const [
    showCreateTourFromUrlModal,
    {
      setTrue: setTrueShowCreateTourFromUrlModal,
      setFalse: setFalseShowCreateTourFromUrlModal,
    },
  ] = useBoolean();

  const handleContinueCreateTour = (cardId: string) => {
    if (cardId === 'demo') {
      setTrueShowCreateTourFromDemoModal();
      return;
    }
    setTrueShowCreateTourFromUrlModal();
  };

  useEffect(() => {
    if (!showCreateTourModal) {
      closeAllModals();
    }
  }, [showCreateTourModal]);

  const [createTourFromUrlMutation] = useCreateTourFromUrlMutation();

  const closeAllModals = () => {
    setFalseShowCreateTourFromDemoModal();
    setFalseShowCreateTourFromUrlModal();
    hide();
  };
  const createTourFromUrl = async (url) => {
    if (!organizationId) {
      return;
    }
    const response = (await createTourFromUrlMutation({
      organizationId,
      url,
      description: 'Tour description',
      tourName: 'Tour name',
      source: isDesktop() ? 'desktop' : 'platform',
    })) as {
      data: Tour & { playerToken: string };
    };
    if (response.data) {
      navigateToWebTourCapture({
        appUrl: url,
        playerToken: response.data.playerToken,
        fileServiceToken: response.data.fileServiceToken,
        tourId: String(response.data.id),
        originalTourId: String(originalTourId),
        insertIndex: selectedFrameIndex + 1,
        workspaceId: response.data.workspaceId,
        folderId,
      });
      closeAllModals();
    }
  };

  const { workspaceId } = useParams<{
    workspaceId?: string;
  }>();
  const { data } = useGetDemosQuery({
    organizationId,
    workspaces: workspaceId,
  });
  const demos = data?.items;

  return (
    <>
      {showCreateTourFromUrlModal && (
        <CreateTourFromScratch
          open={showCreateTourFromUrlModal}
          title={title}
          onSubmit={createTourFromUrl}
          onClose={setFalseShowCreateTourFromUrlModal}
        />
      )}
      {showCreateTourModal && (
        <>
          <CreateEntityModal
            open={showCreateTourModal}
            onClose={hide}
            handleContinue={handleContinueCreateTour}
            base="tour"
            isTourFromUrlDisabled={!demos?.length}
          />
          <CreateTourModal
            open={showCreateTourFromDemoModal}
            closeAllModals={closeAllModals}
            onClose={setFalseShowCreateTourFromDemoModal}
            title={title}
            description={description}
            originalTourId={originalTourId}
            selectedFrameIndex={selectedFrameIndex}
            demoIdsTourIsBasedOn={demoIdsTourIsBasedOn}
            goToDemo={goToDemo}
            createTourFromDemo={navigateToTourFromDemo}
            handleOpenRecorder={handleOpenRecorder}
            goToCreateOverlayDemoPage={goToCreateOverlayDemoPage}
            folderId={folderId}
          />
        </>
      )}
    </>
  );
};
